import type { SurfaceHostedIn } from '@hibro/shared';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import { useT } from '../i18n/i18n';
import { useSurfaceHostedIn } from './surface-hosted-in-context';
import { SurfaceLoadingIndicator } from './surface-loading';

/** Message the iframe page posts when ready for the port. Must match `@hibro/sdk`. */
const IFRAME_SDK_READY = 'hibro:iframe-sdk-ready';
/** Envelope carrying the transferred port to the iframe. Must match `@hibro/sdk`. */
const IFRAME_SDK_PORT = 'hibro:iframe-sdk-port';

/**
 * Bridge to the platform's iframe-surface plumbing (见 9). Injected by the app
 * shell over the preload IPC bridge so the platform package stays free of
 * `window.hibroIframe`. `connect` asks the main process to broker a gated
 * `connect-iframe` MessagePort for one view *instance*; `onPort` delivers each
 * brokered port (a host crash/restart re-delivers a fresh one) for that instance.
 *
 * `instanceKey` is part of the key, not decoration: two documents of one view are
 * two iframes with two gated connections, and keying on the view alone would
 * hand each brokered port to both (§5.7).
 */
export interface IframeBridge {
  connect(
    viewId: string,
    instanceKey: string | undefined,
    hostedIn: SurfaceHostedIn,
  ): void;
  onPort(
    viewId: string,
    instanceKey: string | undefined,
    handler: (port: MessagePort) => void,
  ): () => void;
}

export interface IframeSurfaceProps {
  viewId: string;
  /** Which instance of the view this frame renders (§5.7); undefined = singleton. */
  instanceKey?: string;
  url: string;
  bridge: IframeBridge;
}

/**
 * The `iframe` SurfaceHost (见 9). Mounts a sandboxed `<iframe>` in the
 * renderer DOM (loaded over `hibro-assets://`); it composes naturally with the
 * DOM — clipping, scrolling, and React overlays cover it without any bounds
 * sync or z-order capture/hide protocol.
 *
 * Reverse-call SDK (§3/§5.2): an iframe has no preload, so the gated
 * `connect-iframe` MessagePort is brokered by the main process to this renderer
 * (`bridge.connect` + `bridge.onPort`) and forwarded into the iframe via
 * `postMessage` once the iframe page signals readiness. Ext-host gating/ACL
 * resolves `viewId` to the owning module.
 *
 * The `src` is the same for every instance of a view — identity travels over the
 * channel (`negotiate().instanceKey`), not in the address, so a page cannot be
 * told which document it is by anything an origin can observe (§5.7 / 见 20).
 */
export function IframeSurface({
  viewId,
  instanceKey,
  url,
  bridge,
}: IframeSurfaceProps): ReactNode {
  const t = useT();
  const hostedIn = useSurfaceHostedIn();
  const ref = useRef<HTMLIFrameElement>(null);
  // Which address this frame has finished loading. A remote page can take
  // seconds to answer and the frame paints its own blank white in the meantime,
  // so the surface covers it with the platform loading shape until `load`.
  // Tracked as the loaded URL rather than a boolean so navigating a view to
  // another address counts as loading again without a separate reset effect.
  const [loadedUrl, setLoadedUrl] = useState<string>();

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    // The iframe's origin is the precise postMessage target for the port
    // transfer. On desktop that is `hibro-assets://<moduleId>` (per-module);
    // on web the asset base is `<origin>/_mod/<moduleId>/…`, so the frame
    // shares the workbench origin (见 18 — sandbox is then not an isolation
    // boundary). Fall back to '*' if the origin cannot be derived (e.g. an
    // opaque/sandboxed origin serializes as "null").
    let targetOrigin = '*';
    try {
      const origin = new URL(url).origin;
      targetOrigin = origin && origin !== 'null' ? origin : '*';
    } catch {
      targetOrigin = '*';
    }

    let pendingPort: MessagePort | null = null;
    let iframeReady = false;
    let deliveredOnce = false;

    const deliver = (): void => {
      const frame = el.contentWindow;
      if (iframeReady && pendingPort && frame) {
        const port = pendingPort;
        pendingPort = null;
        frame.postMessage({ type: IFRAME_SDK_PORT }, targetOrigin, [port]);
        deliveredOnce = true;
      }
    };

    // Gated port delivered by the platform (via preload window.postMessage),
    // demuxed to this view by the shell bridge. A reconnect delivers a fresh one.
    const offPort = bridge.onPort(viewId, instanceKey, (port) => {
      pendingPort = port;
      deliver();
    });

    const onWindowMessage = (event: MessageEvent): void => {
      if (event.source !== el.contentWindow) {
        return;
      }
      const data = event.data as { type?: string } | null;
      if (data?.type === IFRAME_SDK_READY) {
        iframeReady = true;
        if (pendingPort) {
          deliver();
        } else if (deliveredOnce) {
          // The iframe reloaded after we already handed it a (now-neutered)
          // port; broker a fresh gated port for the new document.
          bridge.connect(viewId, instanceKey, hostedIn);
        }
      }
    };
    window.addEventListener('message', onWindowMessage);

    // Request the gated port now; the iframe announces readiness once loaded.
    bridge.connect(viewId, instanceKey, hostedIn);

    return () => {
      offPort();
      window.removeEventListener('message', onWindowMessage);
      pendingPort = null;
    };
  }, [viewId, instanceKey, hostedIn, url, bridge]);

  return (
    // Positioned wrapper for the loading cover. It has to fill the host itself:
    // the pane it sits in (`.dv-react-part`) is `display:block; height:100%`, so
    // a content-sized wrapper would collapse the frame to nothing.
    <div className="relative h-full min-h-0 w-full">
      <iframe
        ref={ref}
        src={url}
        title={viewId}
        onLoad={() => setLoadedUrl(url)}
        // Sandboxed (§3): scripts run, but the frame cannot break out to the
        // embedder. On desktop `allow-same-origin` + `hibro-assets://<moduleId>`
        // yields a per-module origin distinct from the workbench; on web the
        // assets are same-origin with the app, so this attribute is **not** an
        // isolation boundary there (见 18 — first-party trusted modules only).
        // `allow-downloads` is what lets a view save a file at all: without it
        // Chromium blocks every download a sandboxed frame initiates, `blob:`
        // and `data:` included. Deliberately still absent:
        // `allow-top-navigation*` (a view must not navigate the workbench away)
        // and `allow-popups-to-escape-sandbox` (a popup keeps these same flags).
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads"
        // Policy-controlled features default to a `self` allowlist, and on
        // desktop the frame (`hibro-assets://<moduleId>`) is cross-origin to the
        // workbench page (`hibro-app://workbench`, or the Vite origin in dev) —
        // so without this the clipboard rejects with `NotAllowedError` there
        // while working on web, where module assets are same-origin (见 9 / 18).
        // `microphone`/`camera` are the same shape and are what an in-frame
        // meeting view needs for `getUserMedia`; without them Chromium fails
        // with `Permissions policy violation: microphone is not allowed in this
        // document`. `display-capture` is listed on the same footing: an
        // in-frame meeting view calls `getDisplayMedia` itself. The entry only
        // opens the policy gate — the **source** is still picked by the main
        // process (`setDisplayMediaRequestHandler` + `desktopCapturer`, 见 06 /
        // `src/main/index.ts`), so a frame gains no way to choose a source or to
        // bypass the user's confirmation.
        allow="clipboard-read; clipboard-write; fullscreen; microphone; camera; display-capture"
        style={{
          // Fills the wrapper above, which in turn fills the pane. `flex:1` does
          // nothing in a `display:block` parent, so the frame used to stick at
          // `min-height: 240` and the rest of the editor looked like a shortened
          // blank window.
          height: '100%',
          width: '100%',
          minHeight: 0,
          border: 'none',
          display: 'block',
        }}
      />
      {/* Covers the frame's own blank paint while it loads. Cleared by `load`,
          which Chromium also fires for its error document — so a page that
          refuses to be framed (X-Frame-Options / frame-ancestors, 见 9) still
          shows that refusal instead of spinning forever. */}
      {loadedUrl !== url ? (
        <div className="absolute inset-0 bg-background">
          <SurfaceLoadingIndicator label={t('surface.view.loading')} />
        </div>
      ) : null}
    </div>
  );
}
